import { AbstractControl, AsyncValidator, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Equipment } from '../../../../models/equipment.model';
import { ContainerGrade } from '../../../../models/container-grade.model';
import { Mode } from '../../../../models/mode.model';
import { inject, Injectable } from '@angular/core';
import { combineLatest, forkJoin, map, Observable, take } from 'rxjs';
import { MasterDataService } from '../../../../services/master-data.service';
import { BulkWorkOrderSharedDataService } from '../../../../services/bulk-work-order-shared-data.service';
import { AemsValidationError } from '../../../../models/aems-validation-error.model';
import { Repair } from '../../../../models/repair.model';
import { BulkWorkOrderContainerSizeError } from '../../../../models/bulk-work-order/bulk-work-order-container-size-error.model';

/**
 * ContainerSizeValidator
 *
 * This service validates selected repair codes against container sizes.
 * Certain repair codes may be restricted from being applied to containers
 * of specific sizes. This validator checks the current list of validated
 * equipment against these restrictions and returns a map of repair codes
 * to the list of equipment that violate the size constraints.
 */
@Injectable({ providedIn: 'root' })
export class ContainerSizeValidator {
  private bulkWorkOrderSharedDataService: BulkWorkOrderSharedDataService;
  constructor() {
    this.bulkWorkOrderSharedDataService = inject(BulkWorkOrderSharedDataService);
  }
  validate(selecetdRepairCodes: string[], repairs: Repair[]): Observable<BulkWorkOrderContainerSizeError> {
    const values = selecetdRepairCodes;
    const availableContainerSizes = [20, 40, 45]; // Valid container sizes
    return combineLatest([
      this.bulkWorkOrderSharedDataService.ValidatedEquipments,
      this.bulkWorkOrderSharedDataService.BulkWorkOrderHeader,
      this.bulkWorkOrderSharedDataService.Mode,
    ]).pipe(
      map(([validEquipments, bulkWorkOrderHeader, mode]) => {
        const errorEquipments = new Map<string, Equipment[]>();
        const validSizes = new Map<string, number[]>();
        const enteredEquipmentNumbers = bulkWorkOrderHeader.equipments.map((x) => x.equipmentNumber);
        const equipments = validEquipments.filter((x) => enteredEquipmentNumbers.includes(x.equipmentNumber ?? ''));
        values.forEach((value) => {
          const erroringEquipmentNumbers: Equipment[] = [];
          let repair = repairs.find((x) => x.repairMode === mode?.code && x.repairCode === value);
          let restrictedContainerSizes = repair?.restrictedContainerSizes ?? [];
          const validContainerSizes = availableContainerSizes.filter((size) => !restrictedContainerSizes.includes(size));

          equipments.forEach((equipment) => {
            const sizeOfEquipment: number = Number(equipment.equipmentSize ?? 0);
            if (equipment.equipmentTypeParentCode === 'CONT' && sizeOfEquipment > 0) {
              const alreadyInErrorList = erroringEquipmentNumbers.some((x) => x.equipmentNumber === equipment.equipmentNumber);
              if (restrictedContainerSizes.includes(sizeOfEquipment) && !alreadyInErrorList) {
                erroringEquipmentNumbers.push(equipment);
              }
            }
          });
          if (erroringEquipmentNumbers.length > 0) {
            errorEquipments.set(value, erroringEquipmentNumbers);
            validSizes.set(value, validContainerSizes);
          }
        });
        const result: BulkWorkOrderContainerSizeError = {
          errorEquipments,
          validSizes,
        };

        return result;
      }),
    );
  }
}
